import { contentToStringWithoutTags, excerpt, removeDiacritics, removeHtmlTags } from '@portal/content-renderer';
import { DayOfWeek, OpeningHoursSpecification } from './jsonld-utils';

export function extractOpeningHours(value: string) {
    if (!value) return;

    const text = removeHtmlTags(value);

    const roDays = ['luni', 'marti', 'miercuri', 'joi', 'vineri', 'sambata', 'duminica'];
    const enDays: DayOfWeek[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    const result: OpeningHoursSpecification[] = [];

    // Split the text by newlines
    const lines = text.split('\n');
    for (let i = 0; i < lines.length; i++) {
        const splitLine = lines[i].split(': ');

        if (splitLine.length === 2) {
            const dayOfWeek: DayOfWeek[] = [];
            let opens: string | null = null;
            let closes: string | null = null;
            const daysSplit = splitLine[0].split('-');
            if (daysSplit.length === 1) {
                const index = roDays.indexOf(removeDiacritics(daysSplit[0].trim().toLowerCase()));
                if (index >= 0) {
                    dayOfWeek.push(enDays[index]);
                }
            } else if (daysSplit.length === 2) {
                const firstIndex = roDays.indexOf(removeDiacritics(daysSplit[0].trim().toLowerCase()));
                const lastIndex = roDays.indexOf(removeDiacritics(daysSplit[1].trim().toLowerCase()));
                if (firstIndex >= 0 && lastIndex >= 0 && firstIndex < lastIndex) {
                    for (let j = firstIndex; j <= lastIndex; j++) {
                        dayOfWeek.push(enDays[j]);
                    }
                }
            }

            const hoursSplit = splitLine[1].split('-');
            if (hoursSplit.length >= 2) {
                const regex = /^(([0-1][0-9])|([2][0-3])):([0-5][0-9])$/;
                if (regex.test(hoursSplit[0].trim())) {
                    opens = hoursSplit[0].trim();
                }
                if (regex.test(hoursSplit[1].trim())) {
                    closes = hoursSplit[1].trim();
                }
            }

            if (dayOfWeek && opens && closes) {
                result.push({
                    dayOfWeek: dayOfWeek as DayOfWeek[],
                    opens,
                    closes,
                });
            }
        }
    }

    return result.length ? result : undefined;
}

export function firstPhoneNumber(value: string) {
    if (!value) return;

    let text = removeHtmlTags(value);

    // Get only the first line from the string
    text = text.split('\n')[0].replace('-', '');

    return text;
}

export function highlightTerm(terms: string[], text: string, summary?: boolean) {
    let firstOccurence: number = -1;
    let latinized = removeDiacritics(text);

    if (terms.some((t) => t.length === 1)) {
        terms = [terms.join(' ')];
    }

    terms.forEach((item) => {
        const re = new RegExp(item, 'i'),
            matches: any = latinized.match(re);

        // We don't want to base our summary on prepositions or short words.
        if (item.length < 3 && firstOccurence !== -1) return;

        if (matches && (firstOccurence === -1 || firstOccurence > matches.index)) {
            firstOccurence = matches.index;
        }
    });

    // Summarize the text if needed
    if (summary) {
        const summaryIndex = Math.max(0, firstOccurence - 50);
        let result = '';

        if (summaryIndex > 0) {
            result += '...';
        }
        result += text.substring(summaryIndex, summaryIndex + 600);
        if (text.length > summaryIndex + 600) {
            result += '...';
        }

        text = result;
        latinized = removeDiacritics(text);
    }

    // Highlight the terms in the text
    terms.forEach((item) => {
        const re = new RegExp(`(${item})`, 'gi');
        //matches = latinized.match(re);

        let match;
        const matches: any[] = [];
        while ((match = re.exec(latinized)) !== null) {
            matches.push(match.index);
        }

        if (matches.length) {
            let newText = '',
                currentIndex = 0;
            for (let i = 0; i < matches.length; i++) {
                newText += text.substring(currentIndex, matches[i]);
                // choosed to use emoji so we don't match any of the html keyword
                newText += `☞${text.substring(matches[i], matches[i] + item.length)}☜`;
                currentIndex = matches[i] + item.length;
            }
            newText += text.substring(currentIndex, text.length);
            text = newText;
            latinized = removeDiacritics(text);
        }
    });

    return text
        .replace(new RegExp('☞', 'g'), `<em class='bg-light-green rounded-sm' style='padding: 0 4px'>`)
        .replace(new RegExp('☜', 'g'), `</em>`);
}

export function cleanSearchTerm(str: string) {
    return removeDiacritics(
        decodeURIComponent(str || '')
            .trim()
            .toLowerCase(),
    )
        .replace(/[^a-z0-9]+/gi, ' ')
        .replace(/  /gi, ' ')
        .trim();
}

export function splitSearchTerm(str: string) {
    return str
        .split(' ')
        .map((t) => t.trim())
        .filter((t, index, self) => t !== '' && self.indexOf(t) === index)
        .sort((a, b) => b.length - a.length);
}

export function normalizeObject(object: string | any) {
    return typeof object === 'string' ? JSON.parse(object) : object;
}

export function normalizeJSONObject(object: any) {
    return JSON.parse(JSON.stringify(object));
}
